<template>
    <div class="poi-detail">
        <template v-if="basicPoiData">
            <POISummary :poi="basicPoiData" :waypointData="waypointData" :favourite="foundFavourite" />

            <ContentLockedInfo v-if="isLoginLocked" />

            <template v-else>
                <!-- tabs for connected pois -->
                <el-tabs class="no-header-space space-between-tabs" v-if="basicPoiData.connectedPois" v-model="activeTab" :stretch="true">
                    <el-tab-pane :label="activePoi.label" name="0">
                        <POIDetailContent :activePoi="activePoi" :foundFavourite="foundFavourite" />
                    </el-tab-pane>
                    <el-tab-pane v-for="connectedPoi in basicPoiData.connectedPois" :key="connectedPoi.id" :label="connectedPoi.label" :name="connectedPoi.id">
                        <POIDetailContent :isChild="true" :poiId="connectedPoi.id" :poiApiType="connectedPoi.apiType" :poiType="connectedPoi.poiType" />
                    </el-tab-pane>
                </el-tabs>

                <POIDetailContent v-else :activePoi="activePoi" :foundFavourite="foundFavourite" />
            </template>

        </template>
        <div class="sidebar__section" v-if="loadingDetail">
            <ContentLoader />
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapGetters } from 'vuex';
import eventBus from '@/utils/event-bus';
import { ONLY_LOGGED_IN_AVAILABLE, getActivePoi, getBasicPreparedPoiData, isFavourite, getSubTypeCategory, getFavouriteType, getCoordinatesObject, getCategory } from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import { isResultMultiPoi } from '@/utils/poi-data/is-multi-poi';
import { getPoiTypeLabel } from '@/utils/poi-detail-labels'
import { updatePOIDetailUrl } from '@/utils/url-utils'
import POISummary from '@/components/POISummary.vue'
import ContentLoader from '@/components/ContentLoader.vue'
import POIDetailContent from '@/components/POIDetailContent'
import ContentLockedInfo from '@/components/ContentLockedInfo'

export default {
    components: {
        POISummary,
        ContentLoader,
        POIDetailContent,
        ContentLockedInfo,
    },
    props: {
    },
    data() {
        return {
            activePoi: false,
            loadingDetail: false,
            activeTab: '0',
        }
    },
    created() {
        this.getDetail();
        this.addListeners()
    },
    beforeDestroy() {
        this.updateCurrentUrl()
        this.hidePoiOnMap()
        this.checkToRemovePoiFromMap()
        this.removeListeners()
    },
    watch: {
        activePoiId(value) {
            if(value) {
                this.getDetail();
            }
        },
        mapFullyLoaded(value) {
            if(value && this.poiDetailFromUrl && !this.activePoi) {
                this.getDetail()
            }
        }
    },
    computed: {
        ...mapGetters(['user', 'activePoiId', 'customActivePoiType', 'customActivePoiSubType', 'user', 'allFavourites', 'poiDetailFromUrl', 'mapFullyLoaded', 'poiToRemoveFromMap', 'selectedPOIs', 'poiResults', 'routeTrafficTime']),
        basicPoiData() {
            if(!this.activePoi) {
                return false;
            }
            let basicData = getBasicPreparedPoiData(this.activePoi.poiType, this.activePoi);
            if(this.activePoi.poiType === 'traffic' && this.routeTrafficTime.isNow === false) { 
                basicData.trackingLabel = 'Traffic Forecast';
            }
            eventBus.$emit('poiDetails', basicData);
            return basicData;
        },
        waypointData() {
            if(!this.activePoi || !this.basicPoiData) {
                return false;
            }
            const coordinates = getCoordinatesObject(this.activePoi.poiType, this.activePoi)
            const category = getCategory(this.activePoi.poiType, this.activePoi)
            const waypointData = {
                lat: coordinates && coordinates.latitude ? coordinates.latitude : false,
                lon: coordinates && coordinates.longitude ? coordinates.longitude : false,
                name: this.basicPoiData.title,
                label: this.basicPoiData.title,
                id: this.basicPoiData.id,
                zip: this.basicPoiData.zip,
                city: this.basicPoiData.city,
                type: 'POI_' + getFavouriteType(this.basicPoiData.type, {...this.basicPoiData, category: category}), // for favourite panel
                category: category,
                address: this.basicPoiData.address
            }
            if(this.activePoiId === 'home' || this.activePoiId === 'work') {
                waypointData.specialPreset = this.activePoiId
            }
            if(this.activePoi.lat && this.activePoi.lon) {
                waypointData.lat = this.activePoi.lat
                waypointData.lon = this.activePoi.lon
            }
            // for shorttermparking
            if(this.activePoi.computed && this.activePoi.computed.centerPoint) {
                waypointData.lat = this.activePoi.computed.centerPoint.latitude
                waypointData.lon = this.activePoi.computed.centerPoint.longitude
            }
            return waypointData;
        },
        foundFavourite() {
            return isFavourite(this.basicPoiData.id)
        },
        isLoginLocked() {
            if(this.user) {
                return false
            } else {
                return ONLY_LOGGED_IN_AVAILABLE.includes(this.activePoi.poiType)
            }
        }
    },
    methods: {
        poiFavouritesUpdateHandler() {
            this.getDetail()
        },
        addListeners() {
            eventBus.$on('poiFavouritesUpdated', this.poiFavouritesUpdateHandler)
        },
        removeListeners() {
            eventBus.$off('poiFavouritesUpdated', this.poiFavouritesUpdateHandler)
        },
        async getDetail() {
            // wait for map to be fully loaded before showing poi detail from url
            if(!this.mapFullyLoaded && this.poiDetailFromUrl) {
                this.loadingDetail = true;
                return
            }

            this.activePoi = getActivePoi();
            if(!this.activePoi || isResultMultiPoi(this.activePoi) || this.activePoi.poiType === 'travelRadar') {
                if(isResultMultiPoi(this.activePoi) && this.activePoi.type === 'ADDRESS') return false // address favourites do not have a detail page
                const poiType = this.activePoi.poiType && this.activePoi.poiType !== 'favourites' ? this.activePoi.poiType : (this.customActivePoiType ? this.customActivePoiType.slice() : this.customActivePoiType);
                const subType = this.customActivePoiSubType

                let apiType = false
                if(subType) {
                    const subTypeCategory = getSubTypeCategory(subType)
                    apiType = subTypeCategory.apiType
                } else {
                    const poiConfig = getPoiConfig(poiType)
                    apiType = poiConfig.apiType;
                }


                try {
                    this.loadingDetail = true;
                    const response = await api.poi.getPOIbyID(apiType, this.activePoiId);
                    let responseData = response.data
                    let showAreaOnMap = false

                    if(this.activePoi.poiType === 'travelRadar' || apiType === 'travel-alert') {
                        showAreaOnMap = true
                    }

                    let activePoiData = {
                        poiType,
                        subType: subType ? subType : false,
                        label: getPoiTypeLabel(poiType),
                        ...responseData,
                    }
                    this.loadingDetail = false;
                    this.activePoi = activePoiData;
                    if(this.mapFullyLoaded && this.poiDetailFromUrl) {
                        this.showPoiOnMap()
                    }
                    if(showAreaOnMap) {
                        this.showPoiAreaOnMap()
                    }
                    eventBus.$emit('triggerPOIDetailScrollbar');
                } catch (e) {
                    console.error(e);
                }
            }

            this.updateCurrentUrl(this.customActivePoiType, this.customActivePoiSubType, this.activePoiId)
        },
        updateCurrentUrl(poiType = false, poiSubType = false, poiId = false) {
            updatePOIDetailUrl(this.$route, poiType, poiSubType, poiId)
        },
        showPoiOnMap() {
            if(this.activePoi) {
                eventBus.$emit('showSinglePoiOnMap', this.activePoi)
                this.$store.commit('setPoiDetailFromUrl', false);
            }
        },
        showPoiAreaOnMap() {
            if(this.activePoi) {
                eventBus.$emit('showPoiAreaOnMap', this.activePoi)
            }
        },
        hidePoiOnMap() {
            eventBus.$emit('hideSinglePoiOnMap', this.activePoi)
        },
        checkToRemovePoiFromMap() {
            // to remove nearby station pins that are drawn from parking detail
            if(this.poiToRemoveFromMap) {
                eventBus.$emit('removePoisFromMap', {type: this.poiToRemoveFromMap})
                // if general poi type is active, show the old ones again
                if(this.selectedPOIs.includes(this.poiToRemoveFromMap)) {
                    const generalResults = this.poiResults[this.poiToRemoveFromMap]
                    eventBus.$emit('showPoisOnMap', {type: this.poiToRemoveFromMap, data: generalResults})
                }
                this.$store.commit('setPoiToRemoveFromMap', false)
            }
        }
    }
}
</script>

<style lang="scss">
.poi-detail {
    $block: &;
}
</style>